import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditor, useSmartProp } from '@smartfire/js-block-engine';
import apiFetch from '@wordpress/api-fetch';
import { ReactComponent as Arrow } from './arrow.svg';
import he from 'he';
import clsx from 'clsx';
import style from './style.module.scss';
import Skeleton from '@mui/material/Skeleton';

export const StepsSection = ({ categories, seeMore }) => {
    const { isEditing } = useEditor();
    const [current, setCurrent] = useState(null);

    useEffect(() => {
        if (categories.length) {
            setCurrent(categories[0]);
        }
    }, [categories]);

    return (
        <div className={clsx(style.steps, isEditing && style.admin)}>
            <ul className={style.categories}>
                {categories.map((category) => (
                    <li
                        key={category.term_id}
                        onMouseEnter={() => setCurrent(category)}
                        className={clsx(current === category && style.current)}
                    >
                        <img src={category.acf?.icon?.url} alt="" />
                        <p>{category.name}</p>
                        <Arrow className={style.arrow} />
                    </li>
                ))}
            </ul>

            {categories.map((category, i) => (
                <StepCategory key={i} category={category} current={current === category} seeMore={seeMore} />
            ))}
        </div>
    );
};

const StepCategory = ({ category, current, seeMore }) => {
    const { isEditing } = useEditor();
    const [availagePages, setAvailagePages] = useState([]);
    const [stepPages, setStepPages] = useSmartProp('stepPages');
    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        const ac = new AbortController();
        setLoading(true);

        apiFetch({
            path: 'wp/v2/pages?categories-steps=' + category?.term_id + '&_embed=1',
            signal: ac.signal,
        })
            .then((res) => {
                setAvailagePages(res);
                setLoading(false);
            })
            .catch((e) => {
                if (!(e instanceof DOMException) || e.code !== e.ABORT_ERR) {
                    console.error(e);
                }
                setLoading(false);
            });

        return () => ac.abort();
    }, [category]);

    useEffect(() => {
        if (!stepPages[category.term_id]?.length) {
            let stepPagesTmp = { ...stepPages };
            stepPagesTmp[category.term_id] = [{}, {}, {}, {}];
            setStepPages(stepPagesTmp);
        }
    }, [category, setStepPages, stepPages]);

    const select = useMemo(() => {
        return availagePages.map((page) => (
            <option value={page.id} dangerouslySetInnerHTML={{ __html: page.title.rendered }} />
        ));
    }, [availagePages]);

    const updatePage = (index, pageId) => {
        let stepPagesTmp = { ...stepPages };
        stepPagesTmp[category.term_id][index] = parseInt(pageId);
        setStepPages(stepPagesTmp);
    };

    const findPage = useCallback((id) => availagePages.find((item) => item.id === id), [availagePages]);

    const pages = useMemo(() => {
        const cols = [];

        if (isEditing) {
            for (let i = 0; i < 4; i++) {
                cols.push([]);
            }
        } else {
            if (stepPages[category?.term_id]?.length) {
                for (let i = 0; i < 4; i++) {
                    if (stepPages[category?.term_id][i] && typeof stepPages[category?.term_id][i] !== 'object') {
                        cols.push(findPage(stepPages[category.term_id][i]));
                    }
                }
            }
        }

        return cols;
    }, [stepPages, category, findPage, isEditing]);

    return (
        <div className={clsx(style.pages, current && style.show)}>
            {!isLoading ? (
                pages.map((page, i) => (
                    <div className={style.page} key={'page_' + i}>
                        {isEditing ? (
                            <>
                                {stepPages[category.term_id] && stepPages[category.term_id][i] && (
                                    <img
                                        src={
                                            findPage(stepPages[category.term_id][i])?._embedded['wp:featuredmedia'][0]
                                                ?.source_url
                                        }
                                        className={style.thumb}
                                        alt=""
                                    />
                                )}
                                <select
                                    onChange={(e) => updatePage(i, e.target.value)}
                                    value={stepPages[category.term_id] && stepPages[category.term_id][i]}
                                >
                                    <option value=""></option>
                                    {select}
                                </select>
                            </>
                        ) : (
                            <a href={page?.link}>
                                {page?._embedded['wp:featuredmedia'] &&
                                    page?._embedded['wp:featuredmedia'][0]?.source_url && (
                                        <img
                                            src={page._embedded['wp:featuredmedia'][0].source_url}
                                            className={style.thumb}
                                            alt=""
                                        />
                                    )}
                                <p>{page && page.title && he.decode(page?.title?.rendered)}</p>
                            </a>
                        )}
                    </div>
                ))
            ) : (
                <div className={clsx(style.pages, style.show)}>
                    {[...Array(4)].map((key) => {
                        return (
                            <Skeleton
                                key={key}
                                variant="rectangular"
                                height={240}
                                width={150}
                                sx={{ bgcolor: 'rgba(250, 250, 250, 0.2)' }}
                                className={style.page}
                            />
                        );
                    })}
                </div>
            )}
            {!isEditing && pages.length > 0 && seeMore && pages[0] && (
                <a href={seeMore} className={clsx(style.page, style.more, style.down)}>
                    Voir plus <Arrow className={style.arrow} />
                </a>
            )}
        </div>
    );
};
